import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
  Flex,
  Badge,
  IconButton,
  Stack,
  Spinner,
  Tag,
  Icon,
} from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { ChevronDownIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import { FilterProto, ObservationColumnProto } from "build-ts/metadata";
import { FilterOperator, ColumnType } from "build-ts/enums";
import ColumnValues from "components/common/ColumnValues";
import { getFilterDisplayString } from "utils/nameUtils";
import { getDateFilterCategory } from "utils/dateUtils";
import GlobalEntity from "containers/batch/GlobalEntity";
import { DateFilterCategory } from "utils/enum";
import { DateFilter } from "build-ts/relative_date";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import { KGObservationProto } from "build-ts/analysis_plan";
import { getNodeData } from "services/AnalysisTreeServices";
import { TbFilterBolt, TbFilterCheck } from "react-icons/tb";
import PropagationModal from "containers/analysis-plan/PropagationModal";
import { AvailableNode, PropagationItem } from "./Interface";

// Define an interface for the combined dropdown items
interface DropdownItem {
  column: ObservationColumnProto;
  type: "attribute" | "temporal";
}

interface FiltersProps {
  currentNodeId: string;
  nonBenchmarkEdges?: ObservationColumnProto[];
  filters: FilterProto[];
  setFilters: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  temporalColumns?: ObservationColumnProto[];
  recommendedFilters?: FilterProto[];
}

const Filters: React.FC<FiltersProps> = ({
  currentNodeId,
  nonBenchmarkEdges,
  filters,
  setFilters,
  temporalColumns,
  recommendedFilters = []
}) => {
  // Destructure context.
  const { nodeMap, updateNode } = useAnalysisPlanContext();
  // State for selected item and values
  const [selectedItem, setSelectedItem] = useState<DropdownItem | null>(null);
  const [attributeValues, setAttributeValues] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for date filter when temporal column is selected
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);
  // State for propagation modal
  const [propagationModal, setPropagationModal] = useState<{
    isOpen: boolean;
    item: PropagationItem | null;
    availableNodes: AvailableNode[];
    shouldOverwrite: boolean;
  }>({
    isOpen: false,
    item: null,
    availableNodes: [],
    shouldOverwrite: true
  });
  const [errorMsg, setErrorMsg] = useState<string>("");
  // State for editing existing filters
  const [editingFilter, setEditingFilter] = useState<FilterProto | null>(null);
  // State to track values to be selected when editing
  const [editingValues, setEditingValues] = useState<string[]>([]);
  
  // Create combined dropdown items (attribute edges + temporal columns)
  const dropdownItems: DropdownItem[] = [];
 // Add attribute edges
  nonBenchmarkEdges?.filter((column) => 
    column.kg_column_header?.column_type === ColumnType.ATTRIBUTE).forEach((column) => {
    dropdownItems.push({
      column: column,
      type: "attribute"
    });
  });

  // Add temporal columns
  temporalColumns?.forEach((column) => {
    dropdownItems.push({
      column: column,
      type: "temporal"
    });
  });

  // Reset date filter and category when selected item changes
  useEffect(() => {
    if (selectedItem && selectedItem.type === "temporal") {
      setDateFilter(undefined);
      setDateFilterCategory(DateFilterCategory.FIXED);
    }
  }, [selectedItem]);

  // Fetch attribute values when an attribute item is selected
  useEffect(() => {
    if (selectedItem && selectedItem.type === "attribute" && selectedItem.column?.kg_column_header?.id) {
      const fetchValues = async () => {
        setIsLoading(true);
        try {
          const currentNode = nodeMap.get(currentNodeId);
          const newKGObservation = KGObservationProto.create({
              metrics: currentNode?.selected_metrics,
              benchmarks: currentNode?.selected_benchmarks,
              columns: [selectedItem?.column],
              // IMPORTANT: Use local filters state instead of currentNode?.selected_filters
              // This prevents a timing issue where the API call happens immediately after filter removal,
              // but the debounced auto-save (500ms delay) hasn't updated the nodeMap yet.
              // Using local state ensures we always have the current filter data for accurate API calls.
              filters: filters,
              calculate_non_measure_only: true,
            });
          const request = AnalysisTreeNodeProto.create({
            id: currentNodeId,
            kg_observation: newKGObservation,
          });

          //Now we call the getNodeData API to get the values instead of the getKGColumnValues API
          const values = await getNodeData(request);
          if(values?.node_results?.error_details) {
            const errorMsg = values?.node_results?.error_details?.map(err => err.error_msg).join(", ");
            setErrorMsg(errorMsg);
          }
          const valuesList = JSON.parse(values?.kg_observation_data?.non_measure_values[0]?.data_value_json_array || "");
          setAttributeValues((valuesList || []).map(String));
        } catch (error) {
          console.error("Error fetching attribute values:", error);
          setAttributeValues([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchValues();
    }
  }, [selectedItem]);

  // Effect to handle setting values when editing (both attribute and temporal)
  useEffect(() => {
    if (editingFilter && selectedItem) {
      if (selectedItem.type === "attribute" 
          && editingValues.length > 0 
          && attributeValues.length > 0) {
        // For attribute filters, set the selected values after attribute values are fetched
        setSelectedValues(editingValues);
        setEditingValues([]); // Clear the editing values after setting
      } else if (selectedItem.type === "temporal" && editingFilter.date_filter) {
        // For temporal filters, set the date filter and category
        setDateFilter(editingFilter.date_filter);
        const category = getDateFilterCategory(editingFilter.date_filter);
        setDateFilterCategory(category);
      }
    }
  }, [editingFilter, selectedItem, attributeValues, editingValues]);

  // Function to add a filter or update an existing one
  const handleAddFilter = () => {
    if (!selectedItem) return;

    let newFilter: FilterProto | null = null;

    if (selectedItem.type === "attribute") {
      // For attribute type, use multi-select values
      if (selectedValues.length === 0) return;
      
      newFilter = FilterProto.create({
        filter_id: uuidv4(),
        kg_column_header: selectedItem?.column?.kg_column_header,
        operator: FilterOperator.EQ,
        values: selectedValues,
      });
    } else if (selectedItem.type === "temporal") {
      // For temporal type, use the DateFilter
      if (!dateFilter) return;
      
      // Create a filter using the date filter data
      newFilter = FilterProto.create({
        filter_id: uuidv4(),
        kg_column_header: selectedItem?.column?.kg_column_header,
        operator: FilterOperator.EQ,
        date_filter: dateFilter,
      });
    }

    if (newFilter) {
      // Add to filters
      setFilters([...filters, newFilter]);

      // Reset state
      setSelectedItem(null);
      setSelectedValues([]);
      setAttributeValues([]);
      setDateFilter(undefined);
      setEditingFilter(null);
      setEditingValues([]);
    }
  };

  // Handle filter propagation click
  const handlePropagateFilterClick = (filter: FilterProto) => {
    // Get all nodes from the nodeMap, excluding the current node
    const allNodeIds = Array.from(nodeMap.keys()).filter(id => id !== currentNodeId);
    const allNodes = allNodeIds.map((id) => {
      const node = nodeMap.get(id);
      return {
        id,
        name: node?.label || `Node ${id}`,
        selected: true,
      };
    });
    
    setPropagationModal({
      isOpen: true,
      item: {
        type: "filter",
        filter: filter
      },
      availableNodes: allNodes,
      shouldOverwrite: true
    });
  };

  // Handle propagation modal node toggle
  const handlePropagationNodeToggle = (nodeId: string) => {
    setPropagationModal(prev => ({
      ...prev,
      availableNodes: prev.availableNodes.map(node => 
        node.id === nodeId 
          ? { ...node, selected: !node.selected }
          : node
      )
    }));
  };

  // Handle propagation modal overwrite toggle
  const handlePropagationOverwriteToggle = (shouldOverwrite: boolean) => {
    setPropagationModal(prev => ({
      ...prev,
      shouldOverwrite
    }));
  };

  // Function to confirm propagation to selected descendants
  const handleConfirmPropagation = () => {
    if (!propagationModal.item || propagationModal.item.type !== "filter" || !propagationModal.item.filter) return;

    const filterToPropagate = propagationModal.item.filter;
    
    // Only use selected descendants for propagation
    const selectedDescendantIds = propagationModal.availableNodes
      .filter(node => node.selected)
      .map(node => node.id);

    selectedDescendantIds.forEach(descendantId => {
      const descendantNode = nodeMap?.get(descendantId);
      if (descendantNode) {
        if (propagationModal.shouldOverwrite) {
          const existingFilterIndex = descendantNode.selected_filters?.findIndex(
            existingFilter => existingFilter.kg_column_header?.id === filterToPropagate?.kg_column_header?.id
          );
          if (existingFilterIndex !== undefined && existingFilterIndex >= 0) {
            descendantNode.selected_filters?.splice(existingFilterIndex, 1);
          }
        }
        descendantNode.selected_filters?.push(filterToPropagate);
        updateNode(descendantNode);
      }
    });

    // Close modal and reset state
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Close propagation modal
  const handleClosePropagation = () => {
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Function to remove a filter
  const handleRemoveFilter = (filterId: string) => {
    setFilters(filters.filter((filter) => filter.filter_id !== filterId));
  };

  // Function to handle editing a filter
  const handleEditFilter = async (filter: FilterProto) => {
    // Remove the filter from the selected filters list first
    setFilters(filters.filter(f => f?.filter_id !== filter?.filter_id));
    // Set the filter being edited
    setEditingFilter(filter);
    // Find the corresponding dropdown item
    const correspondingItem = dropdownItems?.find(item => 
      item?.column?.kg_column_header?.id === filter?.kg_column_header?.id
    );
    
    if (correspondingItem) {
      if (correspondingItem.type === "attribute") {
        // For attribute filters, store the values to be selected after fetch
        setEditingValues(filter.values || []);
      }
      setSelectedItem(correspondingItem);
    }
  };

  // Handler for selecting a new item from dropdown (for adding new filters)
  const handleSelectNewItem = (item: DropdownItem) => {
    setSelectedItem(item);
    // Reset values for new filter creation
    setSelectedValues([]);
    setAttributeValues([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
  };

  // Function to cancel current action (editing or adding)
  const handleCancel = () => {
    // If we were editing a filter, restore it to the filters list
    if (editingFilter) {
      setFilters([...filters, editingFilter]);
    }
    
    // Reset all state
    setEditingFilter(null);
    setSelectedItem(null);
    setSelectedValues([]);
    setAttributeValues([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
    setEditingValues([]);
  };

  // Helper function to get item display name
  const getItemDisplayName = (item: DropdownItem | null): string => {
    return item?.column?.kg_column_header?.name || "Unnamed";
  };
  
  // Check if a filter column is in the recommended filters
  const isRecommendedFilter = (columnId: string): boolean => {
    return recommendedFilters.some(filter => 
      filter.kg_column_header?.id === columnId
    );
  };
  
  return (
    <Stack spacing={3}>
      {/* Column Selection Dropdown */}
      <FormControl>
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            isDisabled={dropdownItems.length === 0 || editingFilter !== null}
            fontWeight="600"
            color="fg.default"
          >
            {editingFilter 
              ? `Editing: ${getItemDisplayName(selectedItem)}`
              : selectedItem
              ? getItemDisplayName(selectedItem)
              : dropdownItems.length === 0
              ? "No columns available"
              : filters.length === 0 ? "Add filter" : "Add more filters"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {dropdownItems?.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleSelectNewItem(item)}
                  _hover={{ bg: "purple.50" }}
                  isDisabled={isRecommendedFilter(item?.column?.kg_column_header?.id || "")}
                  fontWeight="600"
                >
                  <Flex alignItems="center" width="100%">
                    {getItemDisplayName(item)}
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Values Selection - only visible when an item is selected */}
      {selectedItem && (
        <FormControl>
          <FormLabel ml={1}>
            {selectedItem.type === "attribute"
              ? "Select values for " + getItemDisplayName(selectedItem)
              : "Define date for " + getItemDisplayName(selectedItem)}
          </FormLabel>

          {selectedItem.type === "attribute" ? (
            // For attribute columns - show multi-select
            isLoading ? (
              <Flex justify="center" align="center" py={4}>
                <Spinner size="sm" mr={2} />
                <Text mb={0}>Loading values...</Text>
              </Flex>
            ) : (
              <ColumnValues
                contextValues={attributeValues}
                selectedOptions={selectedValues}
                setSelectedOptions={setSelectedValues}
                errorMsg={errorMsg}
              />
            )
          ) : (
            // For temporal columns - show GlobalEntity date filter component
            <Box>
              <GlobalEntity
                key={editingFilter ? "edit" : "new"}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                dateFilterCategory={dateFilterCategory}
                setDateFilterCategory={setDateFilterCategory}
              />
            </Box>
          )}
        </FormControl>
      )}

      {/* Add/Update Filter Button - only visible when an item is selected */}
      {selectedItem && (
        <Flex gap={2} width="100%" alignItems="center" justifyContent="center">
          <Button
            colorScheme="purple"
            onClick={handleAddFilter}
            isDisabled={
              (selectedItem.type === "attribute" && selectedValues.length === 0) ||
              (selectedItem.type === "temporal" && !dateFilter)
            }
            flex={1}
            bg="purple.700"
            leftIcon={<Icon as={TbFilterCheck} boxSize={5} />}
          >
            {editingFilter ? "Update Filter" : "Add Filter"}
          </Button>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={handleCancel}
            flex={1}
          >
            Cancel
          </Button>
        </Flex>
      )}

      {/* Display added filters */}
      {filters.length > 0 && (
        <Box mt={2}>
          <Stack spacing={2}>
            {filters?.map((filter) => (
              <Flex
                key={filter.kg_column_header?.id}
                borderWidth="1px"
                borderRadius="md"
                p={1}
                justifyContent="space-between"
                alignItems="center"
                backgroundColor="gray.50"
                borderColor="purple.700"
              >
                <Flex alignItems="center" gap={2} flexWrap="wrap">
                  <Text fontWeight="700" fontSize="sm" mb={0} ml={1}>
                    {filter.kg_column_header?.name || "Unnamed"}:
                  </Text>
                  <Badge colorScheme="purple" fontSize="sm" textTransform="none" bg="purple.700" color="white">
                    {getFilterDisplayString(filter)}
                  </Badge>
                  {/* Show badge for recommended filter */}
                  {isRecommendedFilter(filter.kg_column_header?.id || "") && (
                    <Badge colorScheme="orange" fontSize="xs">
                      From recommendation
                    </Badge>
                  )}
                </Flex>
                {/* Group action icons together */}
                <Flex alignItems="center" gap={2}>
                  <IconButton
                    aria-label="Edit filter"
                    icon={<Icon as={EditIcon} boxSize={6} color="purple.700" />}
                    onClick={() => handleEditFilter(filter)}
                    size="xs"
                    variant="ghost"
                    colorScheme="green"
                    isDisabled={editingFilter !== null && editingFilter.filter_id !== filter.filter_id}
                  />
                  <IconButton
                    aria-label="Propagate filter"
                    icon={<Icon as={TbFilterBolt} boxSize={6} color="purple.700" />}
                    onClick={() => handlePropagateFilterClick(filter)}
                    size="xs"
                    variant="ghost"
                    colorScheme="green"
                  />
                  <IconButton
                    aria-label="Remove filter"
                    icon={<CloseIcon />}
                    onClick={() => handleRemoveFilter(filter?.filter_id || "")}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                  />
                </Flex>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}

      {/* Recommended Filters Section */}
      {recommendedFilters?.length > 0 && (
        <Box mb={0} borderWidth="1px" borderRadius="md" p={2} bg="purple.50" borderColor="purple.700">
          <Text fontWeight="600" fontSize="sm" mb={1}>
            Inherited
          </Text>
          <Stack spacing={1}>
            {recommendedFilters?.map((filter, index) => (
              <Flex
                key={index}
                borderWidth="1px"
                borderRadius="md"
                p={1}
                alignItems="center"
                backgroundColor="white"
                borderColor="purple.700"
              >
                <Box overflow="hidden" textOverflow="ellipsis">
                  <Text fontSize="sm" mb={0} fontWeight="600" ml={1}>
                    {filter?.kg_column_header?.name || "Unknown Column"}
                      {` = [${getFilterDisplayString(filter)}]`}
                  </Text>
                </Box>
                <Tag size="sm" bg="purple.700" ml="auto" color="white">
                  From Parent
                </Tag>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}
      {/* Propagation Modal */}
      <PropagationModal
        isOpen={propagationModal.isOpen}
        onClose={handleClosePropagation}
        item={propagationModal.item}
        availableNodes={propagationModal.availableNodes}
        shouldOverwrite={propagationModal.shouldOverwrite}
        onToggleOverwrite={handlePropagationOverwriteToggle}
        onToggleNode={handlePropagationNodeToggle}
        onConfirm={handleConfirmPropagation}
      />
    </Stack>
  );
};

export default Filters;
